import React, { useMemo } from 'react';
import { FormDefaults } from './types';
import { APITypes } from '../../../../../primary-record-types';
import { getIntl } from '../../../../../utils/intl';
import { defaultInvitationDefinitionPermissions } from '../../../../../utils/defaultPermissions';
import { FormStack } from '../../../../v2BaseComponents/Stack';
import Text from '../../../../v2BaseComponents/Text';
import { Form, SplitFormRow } from '../../../../v2BaseComponents/Form';
import { SelectOption } from '../../../../v2BaseComponents/Inputs/Select/Select';
import useFeatureToggles from '../../../../../hooks/api/useFeatureToggles';
import { Field } from 'formik';
import { FormikInputs } from '../../../../v2BaseComponents/Inputs';
import { composeValidators, futureDate, getDateFormatValidator, required } from '../../../../../utils/validators';
import MaxWidthRestrictedContent from '../../../../v2BaseComponents/MaxWidthRestrictedContent';

const intl = getIntl('bulk-invitation-form-default-selection', {
    header: 'Select Default Invitation Settings',
    subText:
        'Select the default invitation settings for the clients you are inviting.  You will be able to customize these settings for each client.',
    defaultInvitationDefinition: 'Default Invitation Definition',
    defaultAppPermissions: 'Default App Permissions',
    required: 'Required',
    endDate: 'End Date',
    endDateProvisionedProfileDisclaimer:
        'The provisioned medical profile will be deleted if invitation is not accepted before the end date.',
    provisionMedicalProfile: 'Create Medical Profile Before Acceptance?',
    provisionMedicalProfileHelpText:
        // TODO: Add help docs link once it exists
        "Create a medical profile for the client before they accept the invitation. This will allow you to pre-populate the client's medical profile with data while the invitation is pending. Leave unchecked if the client already uses Primary Record.",
    youCannotInviteYourselfOrOtherProfessionalUsers:
        'You cannot send a client invite to yourself or other professional users.',
    setDefaults: 'Set Defaults',
});

export type FormDefaultSelectionOwnProps = {
    onDefaultSelection: (formDefaults: FormDefaults) => void;
    invitationDefinitions: APITypes.organizationService.ListDefinitionsResponse['invitationDefinitions'];
};

export type FormDefaultSelectionProps = FormDefaultSelectionOwnProps;

const FormDefaultSelection: React.FC<FormDefaultSelectionProps> = (props) => {
    const hasProvisionedMedicalProfilesToggle =
        !!useFeatureToggles().featureToggles.organizationProvisionedMedicalProfiles;
    const defaultFormValues = useMemo<FormDefaults>(
        () => ({
            organizationInvitationDefinitionId: props.invitationDefinitions[0]?.organizationInvitationDefinitionId,
            requestedPermissions: defaultInvitationDefinitionPermissions,
            provisionMedicalProfile: false,
            expirationDateTime: undefined,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const invitationDefinitionSelectOptions = props.invitationDefinitions
        .map<SelectOption>((definition) => ({
            label: definition.name,
            value: definition.organizationInvitationDefinitionId,
            testID: definition.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    return (
        <Form
            fillContainer
            actionContainerSubmit
            initialValues={defaultFormValues}
            onSubmit={(values, helpers) => {
                props.onDefaultSelection({
                    ...values,
                    expirationDateTime: values.provisionMedicalProfile ? undefined : values.expirationDateTime,
                });
                helpers.setSubmitting(false);
            }}
            saveText={intl('setDefaults')}
        >
            {({ values }) => (
                <MaxWidthRestrictedContent>
                    <FormStack>
                        <Text variant="h4">{intl('header')}</Text>
                        <Text variant="body-1">{intl('subText')}</Text>
                        <SplitFormRow>
                            <Field
                                name="organizationInvitationDefinitionId"
                                title={intl('defaultInvitationDefinition')}
                                component={FormikInputs.Select}
                                selectOptions={invitationDefinitionSelectOptions}
                                validate={required}
                                isRequired
                            />
                        </SplitFormRow>
                        <SplitFormRow>
                            {hasProvisionedMedicalProfilesToggle ? (
                                <Field
                                    name="provisionMedicalProfile"
                                    title={intl('provisionMedicalProfile')}
                                    component={FormikInputs.Checkbox}
                                    isRequired
                                    helpText={intl('provisionMedicalProfileHelpText')}
                                />
                            ) : null}
                            {values.provisionMedicalProfile ? null : (
                                <Field
                                    name="expirationDateTime"
                                    title={intl('endDate')}
                                    component={FormikInputs.DateTimePicker}
                                    validate={composeValidators(getDateFormatValidator('MM/DD/YYYY'), futureDate)}
                                    helpText={
                                        values.provisionMedicalProfile
                                            ? intl('endDateProvisionedProfileDisclaimer')
                                            : undefined
                                    }
                                />
                            )}
                        </SplitFormRow>
                    </FormStack>
                </MaxWidthRestrictedContent>
            )}
        </Form>
    );
};

export default React.memo(FormDefaultSelection);
